import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { PageHeader, StatCard } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Wallet, Wrench, ClipboardList, Ban, PackageOpen, Sparkles, ArrowRight,
  Bell, Clock, CalendarClock, TrendingUp, ChevronRight, DollarSign, ScrollText,
} from "lucide-react";
import { motion } from "framer-motion";
import WelcomeChecklistCard from "@/components/WelcomeChecklistCard";

/* ============================================================ */
/* Small building blocks                                        */
/* ============================================================ */

function SkeletonCard({ h = "h-24" }) {
  return <div className={"rounded-lg border border-black/5 bg-zinc-100 animate-pulse " + h} />;
}

function DashboardSkeleton() {
  return (
    <div>
      <div className="h-8 w-1/2 rounded bg-zinc-100 animate-pulse mb-6" />
      <div className="rounded-xl bg-zinc-100 animate-pulse h-40 mb-6" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SkeletonCard /> <SkeletonCard /> <SkeletonCard /> <SkeletonCard />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SkeletonCard h="h-64" /><SkeletonCard h="h-64" /><SkeletonCard h="h-64" />
      </div>
    </div>
  );
}

const URGENCY_STYLES = {
  high: "border-rose-200 bg-rose-50",
  medium: "border-amber-200 bg-amber-50",
  low: "border-zinc-200 bg-zinc-50",
};
const URGENCY_ICON = {
  billing: DollarSign,
  package: PackageOpen,
  governance: CalendarClock,
  arc: ClipboardList,
};

function ReminderRow({ r }) {
  const nav = useNavigate();
  const Icon = URGENCY_ICON[r.kind] || Bell;
  return (
    <button
      onClick={() => r.action_href && nav(r.action_href)}
      className={"w-full rounded-lg border p-3 flex items-start gap-3 text-left transition hover:shadow-sm " +
                 (URGENCY_STYLES[r.urgency] || URGENCY_STYLES.low)}
      data-testid={`reminder-${r.id}`}
    >
      <Icon className={"h-4 w-4 mt-0.5 shrink-0 " +
        (r.urgency === "high" ? "text-rose-600" :
         r.urgency === "medium" ? "text-amber-700" : "text-zinc-600")} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-zinc-900">{r.title}</div>
        <div className="text-xs text-zinc-600 mt-0.5">{r.subtitle}</div>
      </div>
      {r.action_label && (
        <div className="flex items-center gap-1 text-xs font-medium text-zinc-700 shrink-0 pt-0.5">
          {r.action_label} <ChevronRight className="h-3 w-3" />
        </div>
      )}
    </button>
  );
}

const ACTIVITY_ICON = {
  payment: DollarSign,
  maintenance: Wrench,
  package: PackageOpen,
  arc: ClipboardList,
};
const ACTIVITY_ACCENT = {
  payment: "text-emerald-500",
  maintenance: "text-indigo-500",
  package: "text-orange-500",
  arc: "text-zinc-600",
};

function ActivityRow({ a }) {
  const Icon = ACTIVITY_ICON[a.kind] || ScrollText;
  const relative = a.at ? new Date(a.at).toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  }) : "";
  return (
    <div className="flex items-start gap-3 py-2" data-testid={`activity-${a.id}`}>
      <div className={"h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center shrink-0 " + (ACTIVITY_ACCENT[a.kind] || "text-zinc-500")}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-900 truncate">{a.title}</div>
        <div className="text-xs text-zinc-500 truncate">{a.description}</div>
      </div>
      <div className="text-xs text-zinc-400 shrink-0">{relative}</div>
    </div>
  );
}

/* ============================================================ */
/* Main page                                                    */
/* ============================================================ */

export default function ResidentDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/dashboard/resident");
      setD(data);
    })().catch(() => { /* handled by axios interceptor */ });
  }, []);

  if (!d) return <DashboardSkeleton />;

  const balance = d.balance || 0;
  const owes = balance < 0;
  const reminders = d.reminders || [];
  const activity = d.recent_activity || [];
  const meetings = d.upcoming_meetings || [];
  const upcomingPasses = d.upcoming_guest_passes || [];

  return (
    <div data-testid="resident-dashboard">
      <PageHeader
        eyebrow={`${user?.name?.split(" ")[0] || "Resident"} · Unit ${user?.unit || "—"}`}
        title="Your community, at a glance."
        subtitle={d.property?.name}
      />

      {/* Balance hero */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-black/5 bg-zinc-900 text-white p-6 sm:p-8 mb-6 relative overflow-hidden"
      >
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-orange-500/25 blur-3xl" />
        <div className="relative">
          <div className="overline text-zinc-400">Account balance</div>
          <div className={"mt-2 text-4xl sm:text-5xl font-semibold font-display tabular-nums break-all " + (owes ? "text-rose-300" : "text-emerald-300")}
               data-testid="dash-balance">
            ${Math.abs(balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            {owes ? `Amount due · ${d.days_past_due || 0} days past due` : "You're all caught up."}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="dash-pay-btn" onClick={() => nav("/resident/pay")} className="rounded-full bg-white text-black hover:bg-white/90">
              <Wallet className="h-4 w-4 mr-1" /> Make payment
            </Button>
            <Button variant="outline" className="rounded-full border-white/30 text-white bg-white/5 hover:bg-white/10" onClick={() => nav("/resident/ledger")}>
              View ledger
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Welcome checklist — auto-hides at 100% */}
      <div className="mb-6">
        <WelcomeChecklistCard />
      </div>

      {/* Reminders — action-oriented, only render when non-empty */}
      {reminders.length > 0 && (
        <div className="mb-6" data-testid="dash-reminders">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="h-4 w-4 text-orange-500" />
            <h2 className="text-base font-medium text-zinc-900">Waiting on you</h2>
            <Badge variant="outline" className="text-[10px] ml-1">{reminders.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {reminders.map((r) => <ReminderRow key={r.id} r={r} />)}
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Open Maintenance" value={d.open_maintenance} icon={Wrench} testid="rdash-maint" />
        <StatCard label="ARC in Review" value={d.open_arc} icon={ClipboardList} testid="rdash-arc" />
        <StatCard label="Open Violations" value={d.open_violations} icon={Ban} testid="rdash-viol" />
        <StatCard label="Packages" value={d.awaiting_packages} icon={PackageOpen} testid="rdash-pkg" />
      </div>

      {/* Middle row — recent activity + upcoming + AI card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Recent activity */}
        <Card className="p-5 lg:col-span-2 bg-white" data-testid="dash-activity">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
            <div className="overline">Recent activity</div>
          </div>
          {activity.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              Nothing to show yet — activity appears here as you use the community.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {activity.map((a) => <ActivityRow key={`${a.kind}-${a.id}`} a={a} />)}
            </div>
          )}
        </Card>

        {/* AI concierge */}
        <Card className="p-5 lg:col-span-1 bg-gradient-to-br from-indigo-600 to-indigo-800 text-white border-none">
          <div className="flex items-center gap-2 mb-2 opacity-90">
            <Sparkles className="h-4 w-4" /> AI Concierge
          </div>
          <div className="text-xl font-semibold font-display leading-tight">
            Ask anything about the community.
          </div>
          <div className="text-sm opacity-80 mt-1">Pool hours, package pickup, ARC questions — the concierge is available 24/7.</div>
          <Button data-testid="dash-open-concierge" onClick={() => nav("/resident/ai/concierge")} className="mt-4 bg-white text-indigo-800 hover:bg-white/90 rounded-full">
            Start a chat <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Card>
      </div>

      {/* Upcoming meetings + guest passes */}
      {(meetings.length > 0 || upcomingPasses.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {meetings.length > 0 && (
            <Card className="p-5 bg-white" data-testid="dash-meetings">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="h-4 w-4 text-indigo-500" />
                <div className="overline">Upcoming board meetings</div>
              </div>
              <div className="space-y-2">
                {meetings.map((m) => (
                  <div key={m.id} className="rounded border border-zinc-100 p-3">
                    <div className="font-medium text-sm">{m.title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {new Date(m.starts_at).toLocaleString()}
                      {m.location && <> · {m.location}</>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {upcomingPasses.length > 0 && (
            <Card className="p-5 bg-white" data-testid="dash-upcoming-passes">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-orange-500" />
                <div className="overline">Scheduled guest passes</div>
              </div>
              <div className="space-y-2">
                {upcomingPasses.map((p) => (
                  <div key={p.id} className="rounded border border-zinc-100 p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{p.guest_name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Arrives {new Date(p.valid_from).toLocaleString()}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-amber-200 bg-amber-50 text-amber-700">
                      pending
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Alerts */}
      <Card className="p-5 bg-white" data-testid="dash-alerts">
        <div className="overline mb-3">Community alerts</div>
        <div className="space-y-2">
          {(d.alerts || []).map((a) => (
            <div key={a.id} className="rounded-md border border-black/5 p-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="capitalize">{a.severity}</Badge>
                <div className="font-medium">{a.title}</div>
              </div>
              <div className="text-sm text-zinc-600 mt-1">{a.body}</div>
            </div>
          ))}
          {(d.alerts || []).length === 0 && (
            <div className="text-sm text-muted-foreground">No active alerts.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
